import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  MessageSquare, Send, LogOut, Clock, User, Mail, Phone,
  MapPin, FileText, X as CloseIcon, Users, Sparkles,
  CheckCircle2, AlertCircle, Ticket, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const SupportInbox = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [supportMember, setSupportMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const token = localStorage.getItem('supportToken');
    const member = localStorage.getItem('supportMemberData');
    
    if (!token) {
      navigate('/support/login');
      return;
    }

    if (member) {
      setSupportMember(JSON.parse(member));
    }

    fetchInbox();
    
    const interval = setInterval(fetchInbox, 5000);
    
    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    if (selectedSession) {
      fetchSessionDetails(selectedSession);
    }
  }, [selectedSession]);

  const fetchInbox = async () => {
    try {
      const token = localStorage.getItem('supportToken');
      const response = await fetch(`${API_BASE_URL}/api/support/inbox`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        setSessions(data.sessions);
      }
    } catch (error) {
      console.error('Error fetching inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      const token = localStorage.getItem('supportToken');
      const response = await fetch(
        `${API_BASE_URL}/api/support/session/${sessionId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const data = await response.json();
      
      if (data.success) {
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !selectedSession) return;

    const message = inputMessage.trim();
    setInputMessage('');
    setIsTyping(true);

    try {
      const token = localStorage.getItem('supportToken');
      const response = await fetch(`${API_BASE_URL}/api/support/send-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: selectedSession,
          message: message
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessages(data.messages);
        fetchInbox();
        toast.success('Message sent! 📤');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsTyping(false);
    }
  };

  const closeSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('supportToken');
      const response = await fetch(
        `${API_BASE_URL}/api/support/close-session/${sessionId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      
      if (data.success) {
        toast.success('Chat closed successfully! ✅');
        setSelectedSession(null);
        fetchInbox();
      }
    } catch (error) {
      console.error('Error closing session:', error);
      toast.error('Failed to close session');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('supportToken');
    localStorage.removeItem('supportMemberData');
    toast.success('Logged out successfully');
    navigate('/support/login');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const activeSession = sessions.find(s => s.session_id === selectedSession);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-cyan-600 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block mb-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
            <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-yellow-300" />
          </div>
          <p className="text-white font-semibold text-lg">Loading inbox...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 shadow-xl">
        <div className="px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center relative">
              <MessageSquare className="h-6 w-6 text-white" />
              <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-yellow-300 animate-pulse" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                Netix Support Inbox
                {sessions.length > 0 && (
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
                    {sessions.length}
                  </span>
                )}
              </h1>
              <p className="text-sm text-purple-100">
                {supportMember?.name} • {sessions.length} active {sessions.length === 1 ? 'chat' : 'chats'}
              </p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/30 backdrop-blur-sm"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-88px)]">
        {/* Sessions List */}
        <div className="w-96 bg-white border-r border-gray-200 overflow-y-auto shadow-lg">
          <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200 sticky top-0 z-10">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Active Chats ({sessions.length})
            </h2>
          </div>
          
          {sessions.length === 0 ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 mb-4">
                <MessageSquare className="h-8 w-8 text-purple-600" />
              </div>
              <p className="text-gray-600 font-medium">No active chats</p>
              <p className="text-sm text-gray-500 mt-1">New chats will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sessions.map((session) => (
                <div
                  key={session.session_id}
                  onClick={() => setSelectedSession(session.session_id)}
                  className={`p-4 cursor-pointer transition-all hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 ${
                    selectedSession === session.session_id 
                      ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-600' 
                      : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center shadow-md">
                        <User className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">
                          {session.visitor_info?.name || 'Visitor'}
                        </h3>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {getTimeAgo(session.last_activity)}
                        </p>
                      </div>
                    </div>
                    {session.unread_count > 0 && (
                      <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-full px-2 py-1 animate-pulse shadow-lg">
                        {session.unread_count}
                      </span>
                    )}
                  </div>
                  
                  {session.visitor_info && (
                    <div className="text-xs text-gray-600 space-y-1 ml-14">
                      <p className="flex items-center gap-1.5 truncate">
                        <Mail className="h-3 w-3 text-purple-600 flex-shrink-0" />
                        <span className="truncate">{session.visitor_info.email}</span>
                      </p>
                      <p className="flex items-center gap-1.5">
                        <Phone className="h-3 w-3 text-purple-600 flex-shrink-0" />
                        {session.visitor_info.mobile}
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-2 ml-14">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 rounded-full text-xs font-medium">
                      <Ticket className="h-3 w-3" />
                      {session.visitor_info?.connection_type || 'New Query'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-white">
          {selectedSession && activeSession ? (
            <>
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-5 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <User className="h-5 w-5" />
                      </div>
                      {activeSession.visitor_info?.name || 'Visitor'}
                    </h2>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-purple-100">
                      <span className="flex items-center gap-1.5">
                        <Mail className="h-4 w-4" />
                        {activeSession.visitor_info?.email}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Phone className="h-4 w-4" />
                        {activeSession.visitor_info?.mobile}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4" />
                        {activeSession.visitor_info?.city}
                      </span>
                    </div>
                    {activeSession.visitor_info?.issue_type && (
                      <p className="text-sm text-purple-100 mt-2 flex items-start gap-2">
                        <FileText className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">{activeSession.visitor_info.issue_type}</span>
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={() => closeSession(selectedSession)}
                    className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/30 backdrop-blur-sm ml-4"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Close Chat
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-br from-gray-50 to-blue-50">
                {messages.map((msg, index) => (
                  <div
                    key={msg.message_id || index}
                    className={`flex gap-3 ${
                      msg.sender_type === 'support' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {msg.sender_type !== 'support' && (
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-md ${
                        msg.sender_type === 'bot' 
                          ? 'bg-gradient-to-r from-blue-400 to-cyan-400' 
                          : 'bg-gradient-to-r from-green-400 to-emerald-400'
                      }`}>
                        {msg.sender_type === 'bot' ? (
                          <MessageSquare className="h-5 w-5 text-white" />
                        ) : (
                          <User className="h-5 w-5 text-white" />
                        )}
                      </div>
                    )}
                    
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-3 shadow-lg ${
                        msg.sender_type === 'support'
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-br-none'
                          : msg.sender_type === 'bot'
                          ? 'bg-white text-gray-800 rounded-bl-none border-2 border-blue-100'
                          : 'bg-white text-gray-800 rounded-bl-none border-2 border-gray-100'
                      }`}
                    >
                      {msg.sender_name && (
                        <p className={`text-xs font-bold mb-1 ${
                          msg.sender_type === 'support' ? 'text-purple-100' : 'text-gray-600'
                        }`}>
                          {msg.sender_name}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                        {msg.message}
                      </p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_type === 'support' ? 'text-purple-100' : 'text-gray-500'
                      }`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>

                    {msg.sender_type === 'support' && (
                      <div className="h-9 w-9 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                        <User className="h-5 w-5 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                
                {isTyping && (
                  <div className="flex justify-end gap-3">
                    <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl rounded-br-none px-4 py-3 shadow-lg">
                      <div className="flex gap-2">
                        <span className="h-2.5 w-2.5 bg-white rounded-full animate-bounce"></span>
                        <span className="h-2.5 w-2.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                        <span className="h-2.5 w-2.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                      </div>
                    </div>
                    <div className="h-9 w-9 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center shadow-md">
                      <User className="h-5 w-5 text-white" />
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="bg-white border-t-2 border-gray-200 p-5">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type your response..."
                    className="flex-1 px-5 py-3 border-2 border-purple-200 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim()}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-full px-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-2 justify-center">
                  <Clock className="h-3 w-3" />
                  Response time: 30 minutes (10 AM - 6 PM) • Press Enter to send
                </p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50">
              <div className="text-center">
                <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 mb-6">
                  <MessageSquare className="h-12 w-12 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Select a Chat</h3>
                <p className="text-gray-600">Choose a conversation from the list to start helping customers</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportInbox;
